'use client';

import { useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import type { CommonProps } from '@mui/material/OverridableComponent';
import type { FadedEdgesBoxProps } from './types';
type HorizontalProps<T> = FadedEdgesBoxProps<T>;
export function Vertical<T extends CommonProps & {
  children?: React.ReactNode;
  ref?: React.Ref<unknown>;
  sx?: SxProps<Theme>;
}>(props: HorizontalProps<T>) {
  const {
    Component,
    fadeColor = '#fff',
    fadeSize = '12%',
    offsetStart = 10,
    offsetEnd = 10,
    overrideRef,
    showStartFadeByDefault = false,
    showEndFadeByDefault = true,
    ...rest
  } = props;
  const {
    sx,
    children
  } = rest;
  const boxRef = useRef<HTMLDivElement>(null);
  const [showFades, setShowFades] = useState<{
    showStartingFade: boolean;
    showEndingFade: boolean;
  }>({
    showStartingFade: showStartFadeByDefault,
    showEndingFade: showEndFadeByDefault
  });
  useEffect(() => {
    const refToUse = overrideRef ? overrideRef.current : boxRef.current;
    const scrollHandler = () => {
      if (refToUse) {
        const {
          clientHeight,
          scrollHeight,
          scrollTop
        } = refToUse;
        setShowFades(prevState => ({
          ...prevState,
          showStartingFade: scrollTop - offsetStart > 0,
          showEndingFade: !(scrollTop + offsetEnd > scrollHeight - clientHeight)
        }));
      }
    };
    if (refToUse) {
      scrollHandler();
    }
    refToUse?.addEventListener('scroll', scrollHandler, {
      passive: true
    });
    return () => {
      refToUse?.removeEventListener('scroll', scrollHandler);
    };
  }, [offsetStart, offsetEnd, overrideRef?.current]);
  const {
    showStartingFade,
    showEndingFade
  } = showFades;
  const containerCommonValues = {
    position: 'absolute',
    content: '""',
    pointerEvents: 'none',
    zIndex: 1,
    left: 0,
    right: 0,
    transition: 'height 0.2s',
    willChange: 'height'
  };
  return <Component ref={overrideRef ?? boxRef} {...rest} sx={{
    '&:before': {
      ...containerCommonValues,
      background: `linear-gradient(${0}deg,hsla(0,0%,100%,0) 0,${fadeColor})`,
      top: 0,
      height: showStartingFade ? fadeSize : 0
    },
    '&:after': {
      ...containerCommonValues,
      background: `linear-gradient(${180}deg,hsla(0,0%,100%,0) 0,${fadeColor})`,
      bottom: 0,
      height: showEndingFade ? fadeSize : 0
    },
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    overflowY: 'auto',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    ...sx
  }}>
      {children}
    </Component>;
}